import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, ArrowRight, Download, Eraser } from 'lucide-react';
import api from '../../lib/api';

/**
 * Manager RECENT ACTIVITY list.
 *
 * READ-ONLY view of the requests that have already been handled, assembled by
 * the backend from the EXISTING request records + the EXISTING action-history
 * events. Clicking a row hands the row straight to the Command Center's
 * existing request detail dialog (same field names), which already renders the
 * shared ActionTimeline — so there is no second detail view and no second
 * timeline component.
 */
const DOT = (status) => (
    status === 'resolved' || status === 'closed' || status === 'completed' || status === 'returned'
        ? 'bg-emerald-500'
        : status === 'cancelled' || status === 'canceled' || status === 'declined'
            ? 'bg-slate-400 dark:bg-slate-500'
            : 'bg-amber-400'
);

const clock = (iso) => {
    if (!iso) return null;
    try { return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }); }
    catch { return null; }
};
const day = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric' }); }
    catch { return ''; }
};

export default function RecentActivity({ active, orgId, locationIds, vertical, refreshKey, onMeta, onOpen }) {
    const { t } = useTranslation();
    const [rows, setRows] = useState(null);
    const [busy, setBusy] = useState(false);
    const [hidden, setHidden] = useState(0);
    const [confirmClear, setConfirmClear] = useState(false);
    const [working, setWorking] = useState(false);

    const params = useCallback(() => {
        const p = {};
        if (orgId && orgId !== 'all') p.organization_id = orgId;
        if (locationIds && locationIds.length > 0) p.location_ids = locationIds.join(',');
        if (vertical) p.vertical = vertical;
        return p;
    }, [orgId, locationIds, vertical]);

    const load = useCallback(async () => {
        setBusy(true);
        try {
            const r = await api.get('/command-center/recent-activity', { params: { ...params(), limit: 30 } });
            setRows(r.data?.items || []);
            setHidden(r.data?.hidden_count || 0);
            onMeta && onMeta({ unseen: r.data?.unseen_count || 0, lastSeen: r.data?.last_seen_at || null });
        } catch { setRows([]); }
        finally { setBusy(false); }
    }, [params]); // eslint-disable-line

    useEffect(() => { load(); }, [load, refreshKey]);

    // Opening the tab clears this manager's "new since last visit" marker for
    // THIS scope only (a watermark on the existing per-person prefs document).
    useEffect(() => {
        if (!active) return;
        api.post('/command-center/recent-activity/seen', null, { params: params() })
            .then(() => onMeta && onMeta({ unseen: 0 }))
            .catch(() => { });
    }, [active]); // eslint-disable-line

    // CLEAR FROM THE SCREEN ONLY — the records stay in the stats and stay in
    // the CSV export until they are removed at the source.
    const clearAll = async () => {
        setWorking(true);
        try {
            await api.post('/command-center/recent-activity/clear', null, { params: params() });
            setConfirmClear(false);
            onMeta && onMeta({ unseen: 0 });
            await load();
        } catch { /* keep the list as-is on failure */ }
        finally { setWorking(false); }
    };

    const exportCsv = async () => {
        setWorking(true);
        try {
            const r = await api.get('/command-center/recent-activity/export',
                { params: { ...params(), include_cleared: true }, responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
            const a = document.createElement('a');
            a.href = url;
            a.download = `recent-activity-${new Date().toISOString().slice(0, 16).replace(/[:T]/g, '')}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch { /* nothing to download */ }
        finally { setWorking(false); }
    };

    if (!active) return null;
    if (rows === null || (busy && rows.length === 0)) {
        return (
            <div className="py-8 flex justify-center" data-testid="cc-recent-loading">
                <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
            </div>
        );
    }

    const toolbar = (
        <div className="flex items-center justify-between gap-2 pb-2 mb-1 border-b border-slate-200 dark:border-slate-800">
            <span className="text-xs text-slate-500 dark:text-slate-400" data-testid="cc-recent-meta">
                {hidden > 0 && t('commandCenter.recent.clearedNote', { count: hidden })}
            </span>
            <span className="flex items-center gap-2">
                <button type="button" onClick={exportCsv} disabled={working}
                    className="flex items-center gap-1 text-xs text-slate-500 hover:text-sky-600 dark:text-slate-400 dark:hover:text-sky-300 transition-colors disabled:opacity-50"
                    data-testid="cc-recent-export">
                    <Download className="w-3.5 h-3.5" />{t('commandCenter.recent.exportCsv')}
                </button>
                {rows.length > 0 && !confirmClear && (
                    <button type="button" onClick={() => setConfirmClear(true)}
                        className="flex items-center gap-1 text-xs text-slate-500 hover:text-red-500 dark:text-slate-400 dark:hover:text-red-400 transition-colors"
                        data-testid="cc-recent-clear">
                        <Eraser className="w-3.5 h-3.5" />{t('commandCenter.recent.clearAll')}
                    </button>
                )}
                {confirmClear && (
                    <span className="flex flex-col sm:flex-row sm:items-center items-end gap-1.5 sm:gap-2" data-testid="cc-recent-clear-confirm">
                        <span className="text-xs text-slate-500 dark:text-slate-400 text-right sm:text-left max-w-[20rem]">{t('commandCenter.recent.clearHelp')}</span>
                        <span className="flex items-center gap-2 shrink-0">
                        <button type="button" onClick={() => setConfirmClear(false)}
                            className="text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-200"
                            data-testid="cc-recent-clear-cancel">{t('commandCenter.act.cancel')}</button>
                        <button type="button" onClick={clearAll} disabled={working}
                            className="text-xs font-semibold px-2 py-1 rounded bg-red-500/90 hover:bg-red-500 text-white disabled:opacity-50"
                            data-testid="cc-recent-clear-confirm-btn">
                            {working ? '…' : t('commandCenter.recent.clearAll')}
                        </button>
                        </span>
                    </span>
                )}
            </span>
        </div>
    );

    if (rows.length === 0) {
        return (
            <div>
                {toolbar}
                <p className="text-sm text-slate-500 py-6 text-center" data-testid="cc-recent-empty">{t('commandCenter.recent.empty')}</p>
            </div>
        );
    }

    return (
        <div data-testid="cc-recent-wrap">
            {toolbar}
            <div className="divide-y divide-slate-200 dark:divide-slate-800" data-testid="cc-recent-list">
            {rows.map((r) => (
                <button key={`${r.kind}-${r.id}`} type="button" onClick={() => onOpen(r)}
                    data-testid={`cc-recent-${r.id}`}
                    className="w-full text-left py-2.5 group">
                    <div className="flex items-start gap-3">
                        <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${DOT(r.status)}`}
                            data-testid={`cc-recent-dot-${r.id}`} data-status={r.status} />
                        <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                <span className="text-sm font-medium text-slate-800 dark:text-slate-200 capitalize">
                                    {t(`commandCenter.act.dispatch.cat.${r.category}`, String(r.category || r.kind || '').replace(/_/g, ' '))}
                                </span>
                                {r.department && (
                                    <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400"
                                        data-testid={`cc-recent-dept-${r.id}`}>
                                        {t(`commandCenter.act.dept.${r.department}`, String(r.department).replace(/_/g, ' '))}
                                    </span>
                                )}
                                {r.room_number && (
                                    <span className="text-xs text-slate-500">{t('commandCenter.ops.room')} {r.room_number}</span>
                                )}
                                <span className="ml-auto text-xs text-slate-500 capitalize" data-testid={`cc-recent-status-${r.id}`}>
                                    {t(`commandCenter.status.${r.status}`, (r.status || '').replace(/_/g, ' '))}
                                    <span className="ml-1.5 text-slate-400 dark:text-slate-500">{day(r.activity_at)} {clock(r.activity_at)}</span>
                                </span>
                            </div>
                            {(r.person_name || r.handled_by) && (
                                <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400 truncate">
                                    {r.person_name && <span data-testid={`cc-recent-person-${r.id}`}>{r.person_name}</span>}
                                    {r.person_name && r.handled_by && <ArrowRight className="inline w-3 h-3 mx-1 -mt-0.5 text-slate-400" />}
                                    {r.handled_by && (
                                        <span className="text-sky-600 dark:text-sky-300" data-testid={`cc-recent-handled-${r.id}`}>
                                            {t('commandCenter.recent.handledBy')}: {r.handled_by}
                                        </span>
                                    )}
                                </p>
                            )}
                            {r.message && <p className="mt-0.5 text-xs text-slate-500 truncate">{r.message}</p>}
                            <p className="mt-1 flex flex-wrap gap-x-3 text-[11px] text-slate-400 dark:text-slate-500"
                                data-testid={`cc-recent-times-${r.id}`}>
                                {r.created_at && <span>{t('actionHistory.step.created', 'Created')} {clock(r.created_at)}</span>}
                                {r.acknowledged_at && <span>{t('actionHistory.step.acknowledged', 'Acknowledged')} {clock(r.acknowledged_at)}</span>}
                                {r.resolved_at && <span>{t('actionHistory.step.resolved', 'Resolved')} {clock(r.resolved_at)}</span>}
                                {r.cancelled_at && <span>{t('actionHistory.step.cancelled', 'Cancelled')} {clock(r.cancelled_at)}</span>}
                                {r.handling_minutes !== null && r.handling_minutes !== undefined && (
                                    <span className="text-slate-500 dark:text-slate-400" data-testid={`cc-recent-duration-${r.id}`}>
                                        {t('commandCenter.recent.duration')}: {t('commandCenter.recent.minutes', { count: r.handling_minutes })}
                                    </span>
                                )}
                            </p>
                        </div>
                    </div>
                </button>
            ))}
            </div>
        </div>
    );
}
